import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ProfileImageUpload } from "@/components/profile/ProfileImageUpload";
import { useProfile } from "@/features/users/hooks/useProfile";
import { formatPhoneNumber } from "@/lib/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle, RefreshCw, Save } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/profile/")({
  head: () => ({
    meta: [
      {
        title: "Profile - GetHandle",
      },
    ],
  }),
  component: ProfilePage,
});

function ProfilePage() {
  const { profile, updateProfile, uploadImage, removeImage, loading, error } = useProfile();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
  });

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || "",
        email: profile.email || "",
        phoneNumber: profile.phoneNumber || "",
      });
    }
  }, [profile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const updates: any = {};

      // Only include changed fields
      if (formData.name !== profile?.name) {
        updates.name = formData.name.trim();
      }

      if (formData.email !== profile?.email) {
        updates.email = formData.email.trim();
      }

      if (formData.phoneNumber !== profile?.phoneNumber) {
        updates.phoneNumber = formData.phoneNumber.trim() || null;
      }

      // Only update if there are changes
      if (Object.keys(updates).length === 0) {
        toast.info("No changes to save");
        return;
      }

      const success = await updateProfile(updates);

      if (success && updates.email) {
        toast.info("Email updated. Please log in again with your new email.");
        // Optionally navigate to login
        // navigate({ to: "/login" });
      }
    } catch (err: any) {
      console.error("Error updating profile:", err);
      toast.error(err.message || "Failed to update profile");
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageUpload = async (file: File) => {
    await uploadImage(file);
  };

  const handleImageRemove = async () => {
    await removeImage();
  };

  if (!profile) {
    return (
      <div className="container mx-auto max-w-2xl p-4 sm:p-6">
        <Skeleton className="h-8 w-48 mb-6" />
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32 mb-2" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col items-center gap-4">
              <Skeleton className="h-32 w-32 rounded-full" />
            </div>
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-2xl p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Profile Settings</h1>
        <p className="text-muted-foreground mt-1">
          Manage your personal information and preferences
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Personal Information</CardTitle>
          <CardDescription>
            Update your profile information. Changes to your email will require you to log in again.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Image */}
            <div className="flex justify-center py-4">
              <ProfileImageUpload
                currentImageUrl={profile.profileImageUrl}
                userName={profile.name}
                onImageSelect={handleImageUpload}
                onImageRemove={handleImageRemove}
                loading={loading}
              />
            </div>

            {/* Name Field */}
            <div className="space-y-2">
              <Label htmlFor="name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter your name"
                required
                disabled={loading || isSaving}
              />
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email">
                Email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter your email"
                required
                disabled={loading || isSaving}
              />
              {formData.email !== profile.email && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Changing your email will update your login credentials. You'll need to log in
                    again with your new email.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* Phone Number Field */}
            <div className="space-y-2">
              <Label htmlFor="phoneNumber">Phone Number</Label>
              <Input
                id="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                placeholder="Enter your phone number"
                disabled={loading || isSaving}
              />
              {formData.phoneNumber && (
                <p className="text-muted-foreground text-xs">
                  Formatted: {formatPhoneNumber(formData.phoneNumber)}
                </p>
              )}
            </div>

            {/* User Type (Read-only) */}
            <div className="space-y-2">
              <Label>Account Type</Label>
              <Input
                type="text"
                value={profile.type.charAt(0).toUpperCase() + profile.type.slice(1)}
                disabled
                className="bg-muted"
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate({ to: "/dashboard" })}
                disabled={loading || isSaving}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading || isSaving}>
                {isSaving ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
